import React, { createContext, useContext, useState, useEffect } from "react";
import { formatDate } from "../../ReusableComponents/functions";

const OrderDraftContext = createContext(null);

const DEFAULT_DRAFT = {
    selectedCustomer: null,
    selectedProduct: null,
    selectedProducts: [],
    cartItems: [],
    shippingDetails: {
        paymentTerm: "",
        startDate: "",
        cancelDate: "",
        shippingAddress: {
            storeName: "",
            address1: "",
            address2: "",
            city: "",
            state: "",
            postcode: "",
            country: ""
        },
        billingAddress: {
            address1: "",
            address2: "",
            city: "",
            state: "",
            postcode: "",
            country: ""
        }
    },
    signature: null
};

export const OrderDraftProvider = ({ children }) => {
    const [draft, setDraft] = useState(() => {
        try {
            const saved = window.sessionStorage.getItem("orderDraft");
            return saved ? JSON.parse(saved) : DEFAULT_DRAFT;
        } catch (error) {
            console.error("Failed to parse order draft from sessionStorage:", error);
            return DEFAULT_DRAFT;
        }
    });

    useEffect(() => {
        window.sessionStorage.setItem("orderDraft", JSON.stringify(draft));
    }, [draft]);

    const setCustomer = (customer) => {
        setDraft(prev => {
            const updated = {
                ...prev,
                selectedCustomer: customer,
                // Update default shipping/billing addresses from customer details
                shippingDetails: {
                    ...prev.shippingDetails,
                    shippingAddress: {
                        storeName: customer?.StoreName || "",
                        address1: customer?.s_address1 || "",
                        address2: customer?.s_address2 || "",
                        city: customer?.s_city || "",
                        state: customer?.s_state || "",
                        postcode: customer?.s_postcode || "",
                        country: customer?.s_country || ""
                    },
                    billingAddress: {
                        address1: customer?.b_address1 || "",
                        address2: customer?.b_address2 || "",
                        city: customer?.b_city || "",
                        state: customer?.b_state || "",
                        postcode: customer?.b_postcode || "",
                        country: customer?.b_country || ""
                    }
                }
            };
            return updated;
        });
    };

    const setProduct = (product) => {
        setDraft(prev => ({ ...prev, selectedProduct: product }));
    };

    const setProducts = (products) => {
        setDraft(prev => ({ ...prev, selectedProducts: [products, ...prev.selectedProducts] }));
    };

    const removeSelectedProduct = (sku) => {
        setDraft(prev => ({ ...prev, selectedProducts: prev.selectedProducts.filter(product => product.sku !== sku), cartItems: prev.cartItems.filter(item => item.sku !== sku) }));
    }

    const updateCartItemQty = (product, sizeItem, newQty) => {
        setDraft(prev => {
            const currentCart = [...prev.cartItems];
            const customer = prev.selectedCustomer;

            const existingIndex = currentCart.findIndex(
                item => item.sku === sizeItem.sku && item.size === sizeItem.size
            );

            if (newQty <= 0) {
                // Remove if qty is 0 or less
                if (existingIndex > -1) {
                    currentCart.splice(existingIndex, 1);
                }
            } else {
                const agentId = window.sessionStorage.getItem("token_id");
                const agentFirstName = window.sessionStorage.getItem("fname") || "";
                const agentLastName = window.sessionStorage.getItem("lname") || "";
                const agentEmail = window.sessionStorage.getItem("email") || "";
                const agentName = `${agentFirstName} ${agentLastName}`.trim();

                const todayStr = formatDate(new Date()); // MM-DD-YYYY

                const cartItemRecord = {
                    status: "1",
                    factory: product?.Factory || product?.factory || null,
                    revenuesTotal: (newQty * sizeItem.price).toFixed(2),
                    storeName: customer?.StoreName || "",
                    customerId: customer?.customerid || "",
                    customerName: `${customer?.firstname || ""} ${customer?.lastname || ""}`.trim(),
                    sku: sizeItem.sku,
                    size: sizeItem.size,
                    unitQuantity: newQty,
                    unitFinalPrice: sizeItem.price,
                    sales: (newQty * sizeItem.price).toFixed(2),
                    agentId: agentId,
                    agentName: agentName,
                    agentEmail: agentEmail,
                    billingShipToStreet: customer?.b_address1 ?? null,
                    billingShipToCity: customer?.b_city ?? null,
                    billingShipToCountry: customer?.b_country ?? null,
                    billingShipToState: customer?.b_state ?? null,
                    billingShipToPostcode: customer?.b_postcode ?? null,
                    date: todayStr,
                    startDate: prev.shippingDetails.startDate || todayStr,
                    cancelDate: prev.shippingDetails.cancelDate || todayStr,
                    paymentTerm: prev.shippingDetails.paymentTerm || null,
                    color_on_box: sizeItem.material || product?.Material1 || "",
                    product_img: product?.Picture || "",

                    brand: product?.Brand || product?.brand || "",
                    scEmail: customer?.sc_email || "",
                    location1: product?.Location1 || null,
                    location2: product?.Location2 || null,
                    location3: product?.Location3 || null,
                    location4: product?.Location4 || null,
                    location5: product?.Location5 || null,
                    location6: product?.Location6 || null,

                    Material1: product?.Material1 || null,
                    Material2: product?.Material2 || null,
                    Material3: product?.Material3 || null,
                    Material4: product?.Material4 || null,
                    Material5: product?.Material5 || null,
                    Material6: product?.Material6 || null,

                    Size1: product?.Size1 || null,
                    Size2: product?.Size2 || null,
                    Size3: product?.Size3 || null,

                    Price1: product?.Price1 || null,
                    Price2: product?.Price2 || null,
                    Price3: product?.Price3 || null,

                    styleFactory: product?.StyleFactory || product?.styleFactory || "",
                    styleName: product?.Stylename || product?.styleName || product?.StyleName || "",
                    outsoleType: product?.Outsoletype || product?.outsoleType || product?.OutsoleType || "",
                    articleName: product?.ArticleName || product?.articleName || "",

                    shipTo: customer?.s_address1 || customer?.StoreName || "",
                    shipToStreet: customer?.s_address1 || "",
                    shipToStreet2: customer?.s_address2 || "",
                    shipToState: customer?.s_state || "",
                    shipToCity: customer?.s_city || "",
                    shipToPostcode: customer?.s_postcode || "",
                    shipToCountry: customer?.s_country || "",

                    tcEmail: customer?.tc_email || "",
                    phoneNumber: customer?.cell_1 || customer?.StorePhone || customer?.cell_2 || "",

                    orderNotes: product?.Notes || "",
                    stich: product?.Stitch || product?.Stich || product?.stich || "",
                    time: product?.Time || product?.time || "",
                    customerEmail1: customer?.email_1 || customer?.email || "",
                    customerEmail2: customer?.email_2 || "",
                    outsoleColor: product?.Outsolecolor || product?.outsoleColor || product?.OutsoleColor || "",
                    billingShipToStreet2: customer?.b_address2 || "",
                    bow: product?.Bow || product?.bow || "",
                    season: product?.Season || product?.SEASON || product?.season || ""
                };

                if (existingIndex > -1) {
                    currentCart[existingIndex] = {
                        ...currentCart[existingIndex],
                        unitQuantity: newQty,
                        sales: (newQty * currentCart[existingIndex].unitFinalPrice).toFixed(2),
                        revenuesTotal: (newQty * currentCart[existingIndex].unitFinalPrice).toFixed(2)
                    };
                } else {
                    currentCart.push(cartItemRecord);
                }
            }

            return { ...prev, cartItems: currentCart };
        });
    };

    const clearCartForProduct = (sku) => {
        setDraft(prev => ({
            ...prev,
            cartItems: prev.cartItems.filter(item => item.sku !== sku)
        }));
    };

    const clearCart = () => {
        setDraft(prev => ({ ...prev, cartItems: [], selectedProducts: [] }));
    };

    const updateShippingDetails = (details) => {
        setDraft(prev => {
            const updatedShipping = {
                ...prev.shippingDetails,
                ...details
            };

            // Update all cart items with new shipping details so they match
            const updatedCart = prev.cartItems.map(item => ({
                ...item,
                startDate: updatedShipping.startDate || item.startDate,
                cancelDate: updatedShipping.cancelDate || item.cancelDate,
                paymentTerm: updatedShipping.paymentTerm || item.paymentTerm,
                shipTo: updatedShipping.shippingAddress.address1 || item.shipTo,
                shipToStreet: updatedShipping.shippingAddress.address1 || item.shipToStreet,
                shipToStreet2: updatedShipping.shippingAddress.address2 || item.shipToStreet2,
                shipToCity: updatedShipping.shippingAddress.city || item.shipToCity,
                shipToState: updatedShipping.shippingAddress.state || item.shipToState,
                shipToPostcode: updatedShipping.shippingAddress.postcode || item.shipToPostcode,
                shipToCountry: updatedShipping.shippingAddress.country || item.shipToCountry,
                billingShipToStreet: updatedShipping.billingAddress.address1 || item.billingShipToStreet,
                billingShipToStreet2: updatedShipping.billingAddress.address2 || item.billingShipToStreet2,
                billingShipToCity: updatedShipping.billingAddress.city || item.billingShipToCity,
                billingShipToState: updatedShipping.billingAddress.state || item.billingShipToState,
                billingShipToPostcode: updatedShipping.billingAddress.postcode || item.billingShipToPostcode,
                billingShipToCountry: updatedShipping.billingAddress.country || item.billingShipToCountry
            }));

            return {
                ...prev,
                shippingDetails: updatedShipping,
                cartItems: updatedCart
            };
        });
    };

    const setSignature = (sig) => {
        setDraft(prev => ({ ...prev, signature: sig }));
    };

    const clearDraft = () => {
        setDraft(DEFAULT_DRAFT);
        window.sessionStorage.removeItem("orderDraft");
    };

    return (
        <OrderDraftContext.Provider
            value={{
                draft,
                setCustomer,
                setProduct,
                setProducts,
                removeSelectedProduct,
                updateCartItemQty,
                clearCartForProduct,
                clearCart,
                updateShippingDetails,
                setSignature,
                clearDraft
            }}
        >
            {children}
        </OrderDraftContext.Provider>
    );
};

export const useOrderDraft = () => {
    const context = useContext(OrderDraftContext);
    if (!context) {
        throw new Error("useOrderDraft must be used within an OrderDraftProvider");
    }
    return context;
};
